import { Table } from "common/Table/Table"
import CopyText from "common/CopyText"
import { useDebounce } from "hooks/useDebounce"
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react"
import Select from "react-select"
import UserService from "services/user.service"
import { exportToCSV, formatDate } from "utils/funcs"
import { formatPhone } from "./Contacts"

const service = new UserService()

/** Common tenure labels; merged with values from the roster API. */
const TENURE_PRESETS = ["Rookie", "Sophomore", "Veteran"] as const

const HIERARCHY_DIMENSION_OPTIONS: { value: string; label: string }[] = [
  { value: "division_name", label: "Division" },
  { value: "partnership_name", label: "Partnership" },
  { value: "senior_region_name", label: "Senior region" },
  { value: "region_name", label: "Region" },
  {
    value: "senior_management_group_name",
    label: "Senior management group",
  },
  { value: "management_group_name", label: "Management group" },
  { value: "team_name", label: "Team" },
]

const DIMENSION_SELECT_OPTIONS = [
  { value: "", label: "None" },
  ...HIERARCHY_DIMENSION_OPTIONS,
]

const selectStyles = {
  menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 20 }),
}

/** Axios body is { success, data: string[] }; normalize edge cases. */
function extractRosterFieldValues(resp: { data?: unknown }): string[] {
  const body = resp?.data as { data?: unknown } | undefined
  if (!body || typeof body !== "object") return []
  const inner = (body as { data?: unknown }).data
  if (!Array.isArray(inner)) return []
  return inner.map((v) => String(v ?? "").trim()).filter(Boolean)
}

export type EmployeeRosterPagination = {
  pageNumber: number
  pageSize: number
  employeeId: string
  employeeName: string
  email: string
  phone: string
  tenure: string
  hierarchyField: string
  hierarchyValue: string
}

function formatYtd(v: unknown): string {
  if (v == null || v === "") return ""
  const n = typeof v === "number" ? v : Number(v)
  if (!Number.isFinite(n)) return String(v)
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

export const EmployeeRosterPage = () => {
  const columns = useMemo(
    () => [
      {
        key: "employee_id",
        dbColName: "employee_id",
        title: "Employee ID",
        freeze: true,
        freezeIndex: "left-0",
      },
      {
        key: "employee_name",
        dbColName: "employee_name",
        title: "Employee name",
        freeze: true,
        freezeIndex: "left-20",
      },
      { key: "tenure", dbColName: "tenure", title: "Tenure" },
      { key: "division_name", dbColName: "division_name", title: "Division" },
      { key: "partnership_name", dbColName: "partnership_name", title: "Partnership" },
      { key: "senior_region_name", dbColName: "senior_region_name", title: "Senior region" },
      { key: "region_name", dbColName: "region_name", title: "Region" },
      {
        key: "senior_management_group_name",
        dbColName: "senior_management_group_name",
        title: "Senior MGMT group",
      },
      {
        key: "management_group_name",
        dbColName: "management_group_name",
        title: "Management group",
      },
      { key: "team_name", dbColName: "team_name", title: "Team" },
      {
        key: "email",
        dbColName: "email",
        title: "Email",
        render: (email: string) => (
          <CopyText copyText={email} text={email} />
        ),
      },
      {
        key: "phone",
        dbColName: "phone",
        title: "Phone",
        render: (phone: string) => (
          <CopyText copyText={phone} text={formatPhone(phone)} />
        ),
      },
      {
        key: "ytd_fp",
        dbColName: "ytd_fp",
        title: "YTD FP",
        render: (v: unknown) => formatYtd(v),
      },
      {
        key: "ytd_fp_plus",
        dbColName: "ytd_fp_plus",
        title: "YTD FP+",
        render: (v: unknown) => formatYtd(v),
      },
      {
        key: "ytd_prmr",
        dbColName: "ytd_prmr",
        title: "YTD PRMR",
        render: (v: unknown) => formatYtd(v),
      },
      {
        key: "synced_at",
        dbColName: "synced_at",
        title: "Last synced",
        render: (d: unknown) => formatDate(d),
      },
    ],
    []
  )

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [pagination, setPagination] = useState<EmployeeRosterPagination>({
    pageNumber: 1,
    pageSize: 100,
    employeeId: "",
    employeeName: "",
    email: "",
    phone: "",
    tenure: "",
    hierarchyField: "",
    hierarchyValue: "",
  })

  const debounced = useDebounce(pagination, 400)

  const fetchData = useCallback(() => {
    setLoading(true)
    service
      .getEmployeeRoster(debounced)
      .then((resp) => {
        if (resp.data.success) {
          setData((resp.data.data ?? []) as Record<string, unknown>[])
          setTotalItems(resp.data.total_docs ?? resp.data.totalDocument ?? 0)
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false))
  }, [debounced])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleExport = () => {
    setExporting(true)
    service
      .getEmployeeRoster({
        ...debounced,
        isDownload: "true",
      })
      .then((resp) => {
        if (resp.data.success) {
          exportToCSV((resp.data.data ?? []) as Record<string, unknown>[], "employee_roster", {
            columns: columns.map((c) => ({
              dbColName: c.dbColName as string,
              title: c.title,
            })),
          })
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setExporting(false))
  }

  const clearFilter = () => {
    setPagination((p) => ({
      ...p,
      pageNumber: 1,
      employeeId: "",
      employeeName: "",
      email: "",
      phone: "",
      tenure: "",
      hierarchyField: "",
      hierarchyValue: "",
    }))
  }

  return (
    <Fragment>
      <div className="flex flex-col mb-4">
        <h1 className="page-title">
          Employee Roster
        </h1>
        <p className="page-subtitle">
          Search by employee ID, name, email, or phone. Narrow by tenure, or choose an org dimension
          (division, team, management group, etc.) and pick a value to list everyone in that group—then
          refine further with the employee fields above.
        </p>
      </div>
      <Table
        data={data}
        handleExport={handleExport}
        pagination={pagination}
        setData={setData}
        exporting={exporting}
        setPagination={setPagination}
        showSearch={true}
        loadingData={loading}
        filters={
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={clearFilter}
              className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Clear filters
            </button>
          </div>
        }
        columnFilters={
          <RosterColumnFilters pagination={pagination} setPagination={setPagination} />
        }
        totalItems={totalItems}
        columns={columns}
      />
    </Fragment>
  )
}

function RosterColumnFilters({
  pagination,
  setPagination,
}: {
  pagination: EmployeeRosterPagination
  setPagination: React.Dispatch<React.SetStateAction<EmployeeRosterPagination>>
}) {
  const [tenureOptions, setTenureOptions] = useState<{ value: string; label: string }[]>([
    { value: "", label: "All tenures" },
  ])
  const [hierarchyOptions, setHierarchyOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [hierarchyLoading, setHierarchyLoading] = useState(false)

  useEffect(() => {
    service
      .getEmployeeRosterFieldValues({ field: "tenure", q: "" })
      .then((resp) => {
        const fromApi = extractRosterFieldValues(resp)
        const merged = new Set<string>([...TENURE_PRESETS, ...fromApi])
        const sorted = Array.from(merged).sort((a, b) => a.localeCompare(b))
        setTenureOptions([
          { value: "", label: "All tenures" },
          ...sorted.map((t) => ({ value: t, label: t })),
        ])
      })
      .catch(() => {
        setTenureOptions([
          { value: "", label: "All tenures" },
          ...TENURE_PRESETS.map((t) => ({ value: t, label: t })),
        ])
      })
  }, [])

  useEffect(() => {
    const field = pagination.hierarchyField
    if (!field) {
      setHierarchyOptions([])
      setHierarchyLoading(false)
      return
    }
    let cancelled = false
    setHierarchyLoading(true)
    setHierarchyOptions([])
    service
      .getEmployeeRosterFieldValues({ field, q: "" })
      .then((resp) => {
        if (cancelled) return
        const rows = extractRosterFieldValues(resp)
        setHierarchyOptions(rows.map((v) => ({ value: v, label: v })))
      })
      .catch(() => {
        if (!cancelled) setHierarchyOptions([])
      })
      .finally(() => {
        if (!cancelled) setHierarchyLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [pagination.hierarchyField])

  const dimensionValue = useMemo(
    () =>
      DIMENSION_SELECT_OPTIONS.find((o) => o.value === pagination.hierarchyField) ??
      DIMENSION_SELECT_OPTIONS[0],
    [pagination.hierarchyField]
  )

  const tenureValue = useMemo(
    () =>
      tenureOptions.find((o) => o.value === pagination.tenure) ?? tenureOptions[0],
    [tenureOptions, pagination.tenure]
  )

  const hierarchySelectValue = useMemo(() => {
    if (!pagination.hierarchyValue) return null
    return { value: pagination.hierarchyValue, label: pagination.hierarchyValue }
  }, [pagination.hierarchyValue])

  const textField = (
    label: string,
    key: "employeeId" | "employeeName" | "email" | "phone",
    placeholder?: string
  ) => (
    <div className="flex flex-1 flex-col gap-1 min-w-[150px]">
      <label className="field-label">{label}</label>
      <input
        className="rounded-md focus:border-gray-300 font-light focus:outline-none flex-1 focus:ring-0 border-gray-200 px-3 py-2 text-sm text-gray-800"
        type="text"
        placeholder={placeholder ?? `Search ${label.toLowerCase()}`}
        value={pagination[key]}
        onChange={(e) =>
          setPagination({ ...pagination, [key]: e.target.value, pageNumber: 1 })
        }
      />
    </div>
  )

  return (
    <div className="flex flex-col gap-4 mt-3">
      <div className="flex gap-3 flex-wrap items-end">
        {textField("Employee ID", "employeeId")}
        {textField("Employee name", "employeeName")}
        {textField("Email", "email")}
        {textField("Phone", "phone")}
        <div className="flex flex-1 flex-col gap-1 min-w-[160px] max-w-[220px]">
          <label className="field-label">Tenure</label>
          <Select
            className="react-select text-sm"
            classNamePrefix="react-select"
            styles={selectStyles}
            options={tenureOptions}
            value={tenureValue}
            onChange={(opt) =>
              setPagination({
                ...pagination,
                tenure: opt?.value ?? "",
                pageNumber: 1,
              })
            }
            isSearchable
            placeholder="Tenure"
          />
        </div>
      </div>

      <div className="flex gap-3 flex-wrap items-end border-t border-gray-100 pt-3">
        <div className="flex flex-1 flex-col gap-1 min-w-[200px] max-w-[280px]">
          <label className="field-label">Org filter</label>
          <Select
            className="react-select text-sm"
            classNamePrefix="react-select"
            styles={selectStyles}
            options={DIMENSION_SELECT_OPTIONS}
            value={dimensionValue}
            onChange={(opt) =>
              setPagination({
                ...pagination,
                hierarchyField: opt?.value ?? "",
                hierarchyValue: "",
                pageNumber: 1,
              })
            }
            isSearchable={false}
            placeholder="Dimension"
          />
        </div>
        {pagination.hierarchyField ? (
          <div className="flex flex-1 flex-col gap-1 min-w-[260px] max-w-[420px]">
            <label className="field-label">
              {dimensionValue.label}
            </label>
            <Select
              key={pagination.hierarchyField}
              className="react-select text-sm"
              classNamePrefix="react-select"
              styles={selectStyles}
              options={hierarchyOptions}
              value={hierarchySelectValue}
              onChange={(opt) =>
                setPagination({
                  ...pagination,
                  hierarchyValue: opt?.value ?? "",
                  pageNumber: 1,
                })
              }
              isClearable
              isSearchable
              isLoading={hierarchyLoading}
              placeholder={
                hierarchyLoading
                  ? "Loading options…"
                  : hierarchyOptions.length
                    ? `Choose a ${dimensionValue.label.toLowerCase()}…`
                    : "No values in roster for this field"
              }
              noOptionsMessage={() =>
                hierarchyLoading ? "Loading…" : "No matches"
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}
